import { useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { productDetails } from '../../shared/data/productDetails';
import { NotFound } from '../NotFound/index';
import { SEO } from '../../shared/components/SEO';
import '../../shared/styles/z30-product.css';

export function ProductDetailPage() {
  const { productId } = useParams<{ productId: string }>();
  const [activeTab, setActiveTab] = useState<'dosage' | 'store'>('dosage');

  if (!productId || !productDetails[productId]) {
    return <NotFound />;
  }

  const product = productDetails[productId];

  return (
    <div className="z30-product-page">
      <SEO 
        title={`${product.title} | ZFAST Animal Feed`}
        description={`${product.subtitle} - ${product.description.join(' ')}`}
        path={`/products/${product.id}`}
        type="product"
      />
      <main className="page">


        <div style={{ marginBottom: '32px', textAlign: 'left' }}>
          <h1>{product.title}</h1>
          <p className="tagline" style={{ fontSize: '1.2rem' }}>{product.tagline}</p>
        </div>

        <section className="product-top">
          <div className="product-image">
            <img src={product.image} alt={`ZFAST ${product.title} feed packaging and product information`} width="1800" height="1442" />
          </div>

          <div className="product-info">
            <span className="eyebrow">{product.eyebrow}</span>
            <p className="subtitle">{product.subtitle}</p>
            {product.description.map((desc, i) => (
              <p key={i}>{desc}</p>
            ))}
            <div className="why-needed" style={{ marginTop: '20px', padding: '16px', background: '#f5fff7', borderRadius: '8px', border: '1px solid var(--line)' }}>
              <b>Recommended For: </b> {product.recommendedFor}
            </div>
          </div>

          <aside className="spec-card">
            <table className="spec-table">
              <thead>
                <tr>
                  <th>Specification</th>
                  <th>Details</th>
                </tr>
              </thead>
              <tbody>
                {product.specs.map((spec, i) => (
                  <tr key={i}>
                    <td>{spec.label}</td>
                    <td>{spec.value}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            <p className="spec-note">Refer to the final product label for guaranteed analysis and feeding directions.</p>
          </aside>
        </section>

        <section className="detail-row">
          <div className="box">
            <div className="box-title">Benefits</div>
            <ul className="benefit-list">
              {product.benefits.map((benefit, i) => (
                <li key={i}>{benefit}</li>
              ))}
            </ul>
          </div>

          <div className="dosage-box">
            <div className="dosage-tabs" role="tablist" aria-label="Product usage details">
              <button 
                className={`dosage-title ${activeTab === 'dosage' ? 'active' : ''}`} 
                type="button" 
                role="tab" 
                aria-selected={activeTab === 'dosage'} 
                onClick={() => setActiveTab('dosage')}
              >
                Dosage
              </button>
              <button 
                className={`dosage-title ${activeTab === 'store' ? 'active' : ''}`} 
                type="button" 
                role="tab" 
                aria-selected={activeTab === 'store'} 
                onClick={() => setActiveTab('store')}
              >
                How to store?
              </button>
            </div>

            <div className={`dosage-content ${activeTab === 'dosage' ? 'active' : ''}`} role="tabpanel">
              <ul>
                <li>Feed as part of the daily ration based on animal age, milk yield, and body condition.</li>
                <li>Keep clean drinking water available at all times.</li>
                <li>For best results, consult a livestock nutrition expert.</li>
              </ul>
            </div>

            <div className={`dosage-content ${activeTab === 'store' ? 'active' : ''}`} role="tabpanel">
              <ul>
                <li>Store in a cool, dry, and hygienic place.</li>
                <li>Keep the bag closed after opening.</li>
                <li>Protect feed from moisture, direct sunlight, rodents, and pests.</li>
                <li>Do not use wet, spoiled, or fungus-affected feed.</li>
              </ul>
            </div>
          </div>
        </section>

        <section className="related-section">
          <div className="related-head">
            <div>
              <h2 className="related-title">More Related Products</h2>
              <p>Show other feed products just before the footer. This section moves in a loop and pauses on hover.</p>
            </div>
          </div>

          <div className="loop-wrap">
            <div className="loop-track">
              {Object.values(productDetails).map((related) => (
                <article className="related-card" key={related.id}>
                  <Link to={`/products/${related.id}`}>
                    <div className="related-img">
                      <img src={related.image} alt={`${related.title} animal feed product`} loading="lazy" decoding="async" />
                    </div>
                    <strong>{related.title}</strong>
                    <span>{related.tagline}</span>
                    <p>{related.subtitle}</p>
                    <div className="why-needed"><b>Recommended:</b> {related.recommendedFor}</div>
                  </Link>
                </article>
              ))}
              {/* Duplicate cards for seamless loop */}
              {Object.values(productDetails).map((related) => (
                <article className="related-card" key={`${related.id}-dup`}>
                  <Link to={`/products/${related.id}`}>
                    <div className="related-img">
                      <img src={related.image} alt={`${related.title} animal feed product`} loading="lazy" decoding="async" />
                    </div>
                    <strong>{related.title}</strong>
                    <span>{related.tagline}</span>
                    <p>{related.subtitle}</p>
                    <div className="why-needed"><b>Recommended:</b> {related.recommendedFor}</div>
                  </Link>
                </article>
              ))}
            </div>
          </div>
        </section>
      </main>
    </div>
  );
}
